import React, {FC, useState} from 'react';
import {Card} from "antd";
import styled from "styled-components";
import {UserStatus, useUserStatusName} from "../../../../utils/constans/statuses/userStatuses";
import {HandleUserRegistrationModal} from "./HandleUserRegistrationModal";
import {roles} from "../../../../config/acl/roles";
import {IRegistration} from "../../../../store/reducers/registrationsReducer/registrationsReducerTypes";
import {RegistrationLicensesModal} from "./RegistrationLicensesModal";
import {UpdateTrialEndDateModal} from "./UpdateTrialEndDateModal";
import {useIntl} from "react-intl";
import {CommonButtonMessages} from "../../../../intl/commonMessages/CommonButtonMessages";
import {CommonUserMessages} from "../../../../intl/commonMessages/CommonUserMessages";
import {CustomButton} from "../../../../components/UI/AntCustom/CustomButton";
import {CustomCard} from "../../../../components/UI/AntCustom/CustomCard";
import dayjs from "dayjs";
import {defineMessages} from "react-intl";

const CardDescription = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 5px;
`;

const StyledCardMeta = styled(Card.Meta)`
  & > div:first-child {
    width: 100%;
  }
`;

const ButtonGroupStyled = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 5px;
`;

const RegistrationCardMessages = defineMessages({
  tariff: {
    id: "registrationCard.tariff",
    defaultMessage: "Tariff",
  },
  tariffEnd: {
    id: "registrationCard.tariffEnd",
    defaultMessage: "Tariff end date",
  },
  autoRenewal: {
    id: "registrationCard.autoRenewal",
    defaultMessage: "Auto renewal",
  },
  yes: {
    id: "registrationCard.yes",
    defaultMessage: "Yes",
  },
  no: {
    id: "registrationCard.no",
    defaultMessage: "No",
  },
  changeTrialEndDate: {
    id: "registrationCard.changeTrialEndDate",
    defaultMessage: "Change trial end date",
  },
  tariffTypeTrial: {
    id: "registrationCard.tariffTypeTrial",
    defaultMessage: "Trial",
  },
  tariffTypePro: {
    id: "registrationCard.tariffTypePro",
    defaultMessage: "Pro",
  },
  tariffTypePremium: {
    id: "registrationCard.tariffTypePremium",
    defaultMessage: "Premium",
  },
  noTariff: {
    id: "registrationCard.noTariff",
    defaultMessage: "No tariff",
  },
});

const getTariffTypeName = (intl: any, type?: number): string => {
  switch (type) {
    case 1:
      return intl.formatMessage(RegistrationCardMessages.tariffTypeTrial);
    case 2:
      return intl.formatMessage(RegistrationCardMessages.tariffTypePro);
    case 3:
      return intl.formatMessage(RegistrationCardMessages.tariffTypePremium);
    default:
      return intl.formatMessage(RegistrationCardMessages.noTariff);
  }
};

interface IRegistrationCard {
  user: IRegistration;
  onTariffUpdate?: () => void;
}

export const RegistrationCard: FC<IRegistrationCard> = ({user, onTariffUpdate}) => {
  const intl = useIntl();
  const userStatusName = useUserStatusName(user.userStatus);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false);
  const [isLicensesModalOpen, setIsLicensesModalOpen] = useState<boolean>(false);
  const [isUpdateTrialEndDateModalOpen, setIsUpdateTrialEndDateModalOpen] = useState<boolean>(false);

  const currentTariff = user.tariff?.companyTariffPackages?.[0];
  const isTrial = currentTariff?.tariffPackageType === 1;

  return (
    <>
      <CustomCard width={"max"}>
        <StyledCardMeta
          title={`${user.lastName} ${user.firstName} ${user.middleName}`}
          description={
            <CardDescription>
              <div>{intl.formatMessage(CommonUserMessages.email)}: {user.email}</div>
              {user.phone && <div>{intl.formatMessage(CommonUserMessages.phone)}: +{user.phone}</div>}
              <div>{intl.formatMessage(CommonUserMessages.role)}: {intl.formatMessage(roles[user.companyRole].name)}</div>
              <div>{intl.formatMessage(CommonUserMessages.company)}: {user.company.name}</div>
              <div>{intl.formatMessage(CommonUserMessages.status)}: {userStatusName}</div>
              
              {/* Тарифная информация */}
              {currentTariff && (
                <>
                  <div>
                    {intl.formatMessage(RegistrationCardMessages.tariff)}: {getTariffTypeName(intl, currentTariff.tariffPackageType)}
                  </div>
                  {currentTariff.tariffEnd && (
                    <div>
                      {intl.formatMessage(RegistrationCardMessages.tariffEnd)}: {dayjs(currentTariff.tariffEnd).format('DD.MM.YYYY')}
                    </div>
                  )}
                  <div>
                    {intl.formatMessage(RegistrationCardMessages.autoRenewal)}: {currentTariff.autoRenewal 
                      ? intl.formatMessage(RegistrationCardMessages.yes) 
                      : intl.formatMessage(RegistrationCardMessages.no)}
                  </div>
                </>
              )}
              {!currentTariff && (
                <div>{intl.formatMessage(RegistrationCardMessages.noTariff)}</div>
              )}
              
              {/* Кнопка изменения даты триала */}
              {isTrial && currentTariff && (
                <CustomButton 
                  onClick={() => setIsUpdateTrialEndDateModalOpen(true)}
                  style={{width: "100%"}}
                >
                  {intl.formatMessage(RegistrationCardMessages.changeTrialEndDate)}
                </CustomButton>
              )}
              
              <CustomButton onClick={() => setIsLicensesModalOpen(true)}>{intl.formatMessage(CommonUserMessages.licensesAndFiles)}</CustomButton>
              <ButtonGroupStyled>
                {user.userStatus === UserStatus.REGISTERED &&
                  <>
                    <CustomButton danger onClick={() => setIsRejectModalOpen(true)} style={{width: "100%"}}>{intl.formatMessage(CommonButtonMessages.reject)}</CustomButton>
                    <CustomButton type={'primary'} onClick={() => setIsConfirmModalOpen(true)}
                            style={{width: "100%"}}>{intl.formatMessage(CommonButtonMessages.confirm)}</CustomButton>
                  </>
                }
              </ButtonGroupStyled>
              <RegistrationLicensesModal 
                isVisible={isLicensesModalOpen} 
                setIsVisible={setIsLicensesModalOpen}
                operatorCard={user.company.operatorCard}
                registrationCertificate={user.company.registrationCertificate}
                caaOperatorApproval={user.company.caaOperatorApproval}
                easaTcoAuthorisation={user.company.easaTcoAuthorisation}
                faaAuthorization={user.company.faaAuthorization}
              />
            </CardDescription>
          }
        />
      </CustomCard>

      {isConfirmModalOpen &&
        <HandleUserRegistrationModal user={user}
                                     statusId={UserStatus.CONFIRMED}
                                     isVisible={isConfirmModalOpen}
                                     setIsVisible={setIsConfirmModalOpen}/>
      }

      {isRejectModalOpen &&
        <HandleUserRegistrationModal user={user}
                                     statusId={UserStatus.REJECTED}
                                     isVisible={isRejectModalOpen}
                                     setIsVisible={setIsRejectModalOpen}/>
      }

      {isUpdateTrialEndDateModalOpen &&
        <UpdateTrialEndDateModal 
          user={user}
          isVisible={isUpdateTrialEndDateModalOpen}
          setIsVisible={setIsUpdateTrialEndDateModalOpen}
          onSuccess={onTariffUpdate}
        />
      }
    </>
  );
};

